import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {Store} from '@ngrx/store';
import {firstValueFrom, Observable} from 'rxjs';
import {ColDef, GridApi, GridOptions, GridReadyEvent, SelectionChangedEvent} from 'ag-grid-community';

import {
  gridColumnTypes,
} from '../../../../shared-utilities/models-old/ngp-report-grid/defaults/column-types/column-types';
import {
  generalColDefDefaults,
} from '../../../../shared-utilities/models-old/ngp-report-grid/defaults/general/general-col-def-defaults';

import {IAutoOrderAddStockItems} from '../../../../features/auto-ordering/models';
import {IPaginationData} from '../../../shared-models/pagination/pagination-data';
import {
  getPaginationResultsForAddStockItem,
  getTypesenseStockItemsSearchResults,
  setStockSearchAppliedFilter,
} from '../../../../features/auto-ordering/store/auto-ordering.actions';
import {getAdvancedFilterGroups} from '../../../../features/core/store-shared-filter/shared-filter.actions';
import * as StockSearchActions from '../../../../shared-modules/shared-process-grid/store/stock-search.actions';

import {StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {ISearchableFields} from '../../../shared-models/type-sense/default-searchable-fields';
import {IFacetCounts} from '../../../shared-models/type-sense/type-sense-types';
import {IDepartment} from '../../../shared-models/stock/departments';
import {IStore} from '../../../shared-models/store/store';
import {
  getOtherItemsTypesenseShelfTalkersSearchResults,
  getOtherPaginationResultsForShelfTalkerStockItem,
  getPaginationResultsForShelfTalkerStockItem,
  setOtherPageSizeForShelfTalkers,
  setPageSizeForShelfTalkers,
} from '../../../../features/shelf-talkers/store/shelf-talkers.actions';
import {
  fetchStoreDepartments,
  getStoreSuppliersByUserSelectedStore,
} from '../../../../features/core/store-shared/shared.actions';
import {
  selectSuppliersFromSharedAsSearchableField,
} from '../../../../features/shelf-talkers/store/shelf-talkers.selectors';
import {selectDepartmentsBySelectedStore} from '../../../../features/core/store-shared/shared.selectors';
import {ISelectOption} from '../../../shared-models/custom-select/custom-select';
import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';
import {SharedModalUtils} from '../../../shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {SharedGridLoadingOverlayComponent} from '../shared-grid-loading-overlay/shared-grid-loading-overlay.component';
import {TLineColour} from '../../../../features/settings-new/models';
import {IAdvancedFilterGroup} from '../../../../features/settings-new/models/general/advanced-filter-group.interface';
import {selectAdvancedFilterGroups} from '../../../../features/core/store-shared-filter/shared-filter.selectors';
import {selectSelectedStore} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {TypeSenseService} from '../../../shared-services/type-sense/type-sense.service';
import {mapTypeSenseObjectToGeneric} from '../../../shared-utils/typesense-utils/typesense.utils';

@Component({
  selector: 'app-shared-stock-search',
  templateUrl: './shared-stock-search.component.html',
  styleUrls: ['./shared-stock-search.component.scss'],
  standalone: false,
})
export class SharedStockSearchComponent {

  @Output() readonly stockItemsSelected = new EventEmitter<StockItem[]>();
  @Output() readonly removeStockItems = new EventEmitter<StockItem[]>();
  @Output() readonly addExceptionToStockItems = new EventEmitter<StockItem[]>();
  @Output() readonly shelfTalkerSettings = new EventEmitter<string>();
  @Output() readonly printOtherItems = new EventEmitter<StockItem[]>();
  @Output() readonly redrawRows = new EventEmitter<GridApi>();

  @Input() gridColDefs: ColDef[];
  @Input() gridOptions: GridOptions;
  @Input() title = 'Add Stock Items';
  @Input() supplier?: string = '';
  @Input() currentNewItems?: number = 0;
  /** Max list items allowed (e.g. 500 for stock take); used with {@link currentNewItems} when {@link limitItems} is true. */
  @Input() maxItemLimit: number = 30;
  @Input() exceptions?: boolean = false;
  @Input() shelfTalkers?: boolean = false;
  @Input() showDeleteAllPrinted?: boolean = false;
  @Input() isShelfTalkersModal?: boolean = false;
  @Input() isModal?: boolean = true;
  @Input() showPaginationBar?: boolean = false;
  @Input() limitItems?: boolean = true;
  @Input() selectedLayout?: string[] = [];
  @Input() allLayouts?: Record<string, ISelectOption> = {};

  @Input() selectedSearchableFields?: ISearchableFields;
  @Input() selectedDepartmentsAndSubDepartments?: IDepartment[];
  @Input() selectedSuppliers?: ISearchableFields;
  @Input() useStockSearchStore?: boolean = false;
  @Input() stockSearchStore?: IStore;
  @Input() confirmButtonLabel?: string;
  @Input() hideViewSelected?: boolean = false;
  @Input() hideSelectAll?: boolean = false;
  @Input() singleSelection?: boolean = false;

  stockItems: StockItem[] = [];
  paginationData: IPaginationData;
  searchString = '';
  gridApi: GridApi;
  currentTablePage: number;
  itemsPerPage: number;
  totalItems: number;
  currentSearch: string;
  totalPages: number;
  selectedStockItemsMap: Map<string, StockItem> = new Map<string, StockItem>();
  preview = false;
  applyFilter = false;
  pageSizeOptions: number[] = [10, 15, 20, 50, 100];
  showRefinements = false;
  facetCounts: IFacetCounts;
  lineColours: TLineColour;
  suppliersObservable$: Observable<ISearchableFields>;
  showFilters = false;
  departmentsObservable$: Observable<IDepartment[]>;
  advancedFiltersObservable$: Observable<IAdvancedFilterGroup[]>;
  selectedAdvancedFilter: IAdvancedFilterGroup | null = null;
  isSelectingAll = false;
  loadingOverlay = SharedGridLoadingOverlayComponent;
  isLoading = true;
  protected readonly columnTypes = gridColumnTypes;
  protected readonly generalColDefDefaults = generalColDefDefaults;
  protected readonly isNaN = isNaN;
  private isSyncingSelection = false;
  private filters: Record<string, ISearchableFields>;

  constructor(
    private readonly store: Store,
    private readonly typeSenseService: TypeSenseService,
    private modalController: ModalControllerService,
    private readonly sharedModalUtils: SharedModalUtils,
    private cdr: ChangeDetectorRef,
  ) {
    this.advancedFiltersObservable$ = this.store.select(selectAdvancedFilterGroups);
  }

  get selectedStockItemCount(): number {
    return this.selectedStockItemsMap.size;
  }

  get remainingItemSlots(): number {
    if (!this.limitItems) {
      return Number.MAX_SAFE_INTEGER;
    }
    return Math.max(0, this.maxItemLimit - (this.currentNewItems ?? 0));
  }

  get isAtItemSelectionLimit(): boolean {
    return this.limitItems && this.selectedStockItemCount >= this.remainingItemSlots;
  }

  get getSelectedLayout(): string[] {
    return this.selectedLayout;
  }

  get getPreviewButtonData(): string {
    if (!this.shelfTalkers) {
      if (this.preview) {
        return `View All Stock Items`;
      } else {
        return `View Selected Items`;
      }
    } else {
      return `Delete Selected Items`;
    }

  }

  get getGridOptions(): GridOptions {
    return {
      ...this.gridOptions,
      loadingOverlayComponent: SharedGridLoadingOverlayComponent,
    };
  }

  get overlayTemplate(): string {
    let template = '';
    if (isNaN(this.totalItems)) {
      template = '<span class="ag-overlay-no-rows-center"><span class="spinner" style="width:24px;height:24px;" aria-hidden="true"></span></span>';
    } else {
      template = '<span class="ag-overlay-no-rows-center">No Items To Display</span>';
    }
    return template;
  }

  @Input() set setStockData(typesenseStockData: IAutoOrderAddStockItems | StockItem[]) {
    if (!typesenseStockData) {
      return;
    }
    if (!Array.isArray(typesenseStockData)) {
      // typesenseStockData is IAddStockItems
      this.showFilters = true;
      this.stockItems = typesenseStockData.stockItems ?? [];
      this.paginationData = typesenseStockData.paginationData;
      this.facetCounts = typesenseStockData.facetCounts;
      this.lineColours = typesenseStockData.lineColour;
      this.currentTablePage = typesenseStockData.paginationData?.currentPage ?? 1;
      if (this.gridApi) {
        this.gridApi.setGridOption('paginationPageSize', typesenseStockData.paginationData?.pageSize);
      }
      this.itemsPerPage = typesenseStockData.paginationData?.pageSize;
      this.totalItems = typesenseStockData.paginationData?.totalItems;
      this.currentSearch = typesenseStockData.paginationData?.searchedValue;
    } else {
      const dataArray = typesenseStockData;
      this.stockItems = dataArray;
      this.itemsPerPage = dataArray.length;
      this.totalItems = dataArray.length;
      this.totalPages = 1;
    }
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    this.isLoading = false;
    this.cdr.markForCheck();
  }


  private readonly selectionChangedHandler = (event: SelectionChangedEvent): void => {
    this.updateSelectedStockItems(event);
    if (
      this.singleSelection
      && event?.source !== 'api'
      && this.selectedStockItemsMap.size === 1
    ) {
      this.onGetSelectedStockItemsClick();
    }
  };

  onGridReady(params: GridReadyEvent): void {
    this.store.dispatch(getStoreSuppliersByUserSelectedStore({dispatch: true}));
    this.store.dispatch(fetchStoreDepartments());
    if (this.isShelfTalkersModal) {
      this.store.dispatch(getAdvancedFilterGroups());
    }
    this.suppliersObservable$ = this.store.select(selectSuppliersFromSharedAsSearchableField);
    this.departmentsObservable$ = this.store.select(selectDepartmentsBySelectedStore);
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
    this.redrawRows.emit(this.gridApi);
    this.gridApi.setGridOption('onRowDataUpdated', () => {
      this.restoreSelectedRows();
    });
    this.gridApi.addEventListener('selectionChanged', this.selectionChangedHandler);
    this.gridApi.sizeColumnsToFit();
  }

  onPaginationChanged(): void {
    this.cdr.detectChanges();
  }

  updateSelectedStockItems(event?: SelectionChangedEvent): void {
    if (this.isSyncingSelection || this.gridApi === undefined) {
      return;
    }
    let selectedNodes;
    try {
      selectedNodes = this.gridApi.getSelectedNodes();
    } catch (error) {
      console.warn('getSelectedNodes not available, skipping selection update:', error);
      return;
    }
    const newlySelectedItems: StockItem[] = selectedNodes.map((node): StockItem => node.data as StockItem);
    const previousSelection = new Map<string, StockItem>(this.selectedStockItemsMap);
    let mergedSelection: Map<string, StockItem>;

    if (this.singleSelection) {
      mergedSelection = new Map<string, StockItem>();
      for (const item of newlySelectedItems) {
        mergedSelection.set(item.code, item);
      }
    } else {
      mergedSelection = new Map<string, StockItem>(this.selectedStockItemsMap);
      const displayedItems: StockItem[] = [];
      this.gridApi.forEachNode((node): void => {
        if (node.data) {
          displayedItems.push(node.data as StockItem);
        }
      });
      displayedItems.forEach((item: StockItem) => {
        if (mergedSelection.has(item.code) && event?.source === 'rowClicked') {
          mergedSelection.delete(item.code);
        }
      });
      for (const item of newlySelectedItems) {
        mergedSelection.set(item.code, item);
      }
    }

    if (this.limitItems && mergedSelection.size > this.remainingItemSlots) {
      if (mergedSelection.size > previousSelection.size) {
        this.showItemLimitReachedModal();
      }
      this.selectedStockItemsMap = previousSelection;
      this.syncGridSelectionToMap();
      this.cdr.detectChanges();
      return;
    }

    this.selectedStockItemsMap = mergedSelection;
    this.cdr.detectChanges();
  }

  private syncGridSelectionToMap(): void {
    if (!this.gridApi) {
      return;
    }
    this.isSyncingSelection = true;
    this.gridApi.removeEventListener('selectionChanged', this.selectionChangedHandler);
    this.gridApi.forEachNode((node) => {
      const code = (node.data as StockItem)?.code;
      node.setSelected(!!code && this.selectedStockItemsMap.has(code));
    });
    this.gridApi.addEventListener('selectionChanged', this.selectionChangedHandler);
    this.isSyncingSelection = false;
  }

  private showItemLimitReachedModal(): void {
    const remainingSlots = this.remainingItemSlots;
    void this.sharedModalUtils.createModal({
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      modalTitle: 'Item Limit Reached',
      contextDescription: [
        remainingSlots === 0
          ? `You have already reached the maximum of ${this.maxItemLimit} selected items.`
          : `You can select at most ${this.maxItemLimit} items.`,
      ],
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseText: 'OK',
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-small'],
    });
  }

  restoreSelectedRows(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.forEachNode((node) => {
      if (this.selectedStockItemsMap.has((node.data as StockItem)?.code)) {
        node.setSelected(true);
      }
    });
  }

  searchOnKeyUp(searchString: string): void {
    this.searchString = searchString;
    if (!this.gridApi) {
      return;
    }
    this.gridApi.removeEventListener('selectionChanged', this.selectionChangedHandler);

    if (this.singleSelection) {
      this.selectedStockItemsMap = new Map();
      this.gridApi.deselectAll();
    }

    if (this.useStockSearchStore && this.stockSearchStore) {
      this.store.dispatch(
        StockSearchActions.searchStockItemsAdvanced({
          query: this.searchString,
          store: this.stockSearchStore,
          filters: this.filters,
        }),
      );
    } else if (!this.shelfTalkers && !this.isShelfTalkersModal) {
      this.store.dispatch(
        getTypesenseStockItemsSearchResults({idField: this.searchString, filters: this.filters}),
      );
    } else if (this.isShelfTalkersModal) {
      this.store.dispatch(
        getOtherItemsTypesenseShelfTalkersSearchResults({
          idField: this.searchString,
          filters: this.filters,
          advancedFilters: this.selectedAdvancedFilter ?? undefined,
        }),
      );
    } else {
      if (this.gridApi) {
        this.gridApi.setGridOption(
          'quickFilterText',
          this.searchString,
        );
      }
    }

    this.gridApi.addEventListener('selectionChanged', this.selectionChangedHandler);
  }

  nextPage(): void {
    if (this.currentTablePage < this.totalPages) {
      this.currentTablePage++;
      this.loadPage();
    }
  }

  onGoToFirstPageClick(): void {
    if (!this.gridApi) {
      return;
    }
    this.currentTablePage = 1;
    this.loadPage();
    this.gridApi.paginationGoToFirstPage();
  }

  onGoToLastPageClick(): void {
    if (!this.gridApi) {
      return;
    }
    this.currentTablePage = this.totalPages;
    this.loadPage();
    this.gridApi.paginationGoToLastPage();
  }

  prevPage(): void {
    if (!this.gridApi) {
      return;
    }
    if (this.currentTablePage > 1) {
      this.currentTablePage--;
      this.loadPage();
      this.gridApi.paginationGoToPreviousPage();
    }
  }

  onPageSizeChange(event: Event): void {
    if (!this.gridApi) {
      return;
    }
    const newPageSize = (event.target as HTMLSelectElement).value;
    this.itemsPerPage = +newPageSize;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    this.currentTablePage = 1;
    this.gridApi.setGridOption('paginationPageSize', this.itemsPerPage);

    if (this.useStockSearchStore) {
      this.store.dispatch(StockSearchActions.setStockSearchPageSize({pageSize: this.itemsPerPage}));
    } else if (this.isShelfTalkersModal && !this.shelfTalkers) {
      this.store.dispatch(setOtherPageSizeForShelfTalkers({pageSize: this.itemsPerPage}));
    } else if (this.shelfTalkers) {
      this.store.dispatch(setPageSizeForShelfTalkers({pageSize: this.itemsPerPage}));
    }

    this.loadPage();
  }

  loadPage(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.removeEventListener('selectionChanged', this.selectionChangedHandler);

    if (this.useStockSearchStore) {
      this.store.dispatch(StockSearchActions.getStockSearchPage({pageNumber: this.currentTablePage}));
    } else if (!this.shelfTalkers && !this.isShelfTalkersModal) {
      this.store.dispatch(getPaginationResultsForAddStockItem({pageNumber: this.currentTablePage}));
    } else if (this.isShelfTalkersModal) {
      this.store.dispatch(getOtherPaginationResultsForShelfTalkerStockItem({
        pageNumber: this.currentTablePage,
        advancedFilters: this.selectedAdvancedFilter ?? undefined,
      }));
    } else {
      this.store.dispatch(getPaginationResultsForShelfTalkerStockItem({pageNumber: this.currentTablePage}));
    }

    this.gridApi.addEventListener('selectionChanged', this.selectionChangedHandler);
  }

  isFirstPageDisabled(): boolean {
    return this.currentTablePage === 1;
  }

  isLastPageDisabled(): boolean {
    return this.currentTablePage === this.totalPages;
  }

  onAddExceptionClick(): void {
    if (!this.gridApi) {
      return;
    }
    const selectedItems = Array.from(this.selectedStockItemsMap.values());
    this.addExceptionToStockItems.emit(selectedItems);
    this.selectedStockItemsMap = new Map();
    this.gridApi.deselectAll();
    this.gridApi.refreshCells();
  }

  onViewSelectedClick(): void {
    if (!this.gridApi) {
      return;
    }
    if (!this.shelfTalkers) {
      this.gridApi.removeEventListener('selectionChanged', this.selectionChangedHandler);
      this.preview = !this.preview;
      if (this.preview) {
        const selectedArray = Array.from(this.selectedStockItemsMap.values());
        this.gridApi.setGridOption('rowData', selectedArray);
        this.restoreSelectedRows();
      } else {
        this.gridApi.setGridOption('rowData', this.stockItems);
        this.restoreSelectedRows();
      }
      this.gridApi.addEventListener('selectionChanged', this.selectionChangedHandler);
    } else {
      const selectedItems = Array.from(this.selectedStockItemsMap.values());
      this.removeStockItems.emit(selectedItems);
      this.selectedStockItemsMap = new Map();
      this.gridApi.deselectAll();
      this.redrawRows.emit(this.gridApi);
    }

  }

  onGetSelectedStockItemsClick(): void {
    if (!this.gridApi) {
      return;
    }
    const selectedItems = Array.from(this.selectedStockItemsMap.values());
    this.stockItemsSelected.emit(selectedItems);
    this.selectedStockItemsMap = new Map();
    this.gridApi.deselectAll();
  }

  deselectAll(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.deselectAll();
    this.selectedStockItemsMap = new Map();
  }

  close = (): void => {
    void this.modalController.dismiss();
  };


  onToggleSupplierOnlyClick(isChecked: boolean): void {
    this.applyFilter = isChecked;
    if (this.applyFilter) {
      const appliedFilter: Record<string, ISearchableFields> = {
        ['40']: {
          [this.supplier]: {
            name: 'supplierId',
            value: true,
          },
        },
      };
      this.store.dispatch(setStockSearchAppliedFilter({filter: appliedFilter}));
    } else {
      this.store.dispatch(setStockSearchAppliedFilter({filter: {}}));
    }

    this.searchOnKeyUp(this.searchString);
  }

  onShowRefinementsClick(event: Event): void {
    event.stopPropagation();
    this.showRefinements = true;
  }

  onHideRefinementsClick(): void {
    this.showRefinements = false;
  }

  deleteAllPrinted(): void {
    if (!this.gridApi) {
      return;
    }
    this.removeStockItems.emit(this.stockItems);
    this.selectedStockItemsMap = new Map();
    this.gridApi.deselectAll();
  }

  onUpdateFilters(event: Record<string, ISearchableFields>): void {
    this.filters = event;
    this.searchOnKeyUp(this.searchString);
  }

  /**
   * Handles an advanced saved-filter selection from the refinements panel.
   * Sets or clears the selected filter group; the subsequent emitFilters event
   * from StockRefinementsComponent will call onUpdateFilters which re-triggers the search.
   */
  onUpdateFilterGroup(event: { advancedFilterGroup: IAdvancedFilterGroup; type: string }): void {
    this.selectedAdvancedFilter = event.type === 'add' ? event.advancedFilterGroup : null;
    this.searchOnKeyUp(this.searchString);
  }

  /**
   * Selects all items matching the current search and filters across all Typesense pages.
   * Fetches up to 250 items per request and accumulates them into selectedStockItemsMap.
   * Shows a loading state while fetching.
   */
  async onSelectAllClick(): Promise<void> {
    if (this.isSelectingAll || !this.totalItems) {
      return;
    }
    this.isSelectingAll = true;
    this.cdr.detectChanges();

    try {
      const store = await firstValueFrom(this.store.select(selectSelectedStore));
      if (!store) {
        return;
      }

      const remainingSlots = this.remainingItemSlots;
      if (remainingSlots === 0) {
        this.showItemLimitReachedModal();
        return;
      }
      const pageSize = 250;
      const totalPages = Math.ceil(this.totalItems / pageSize);
      const searchableFields: ISearchableFields = {
        code: {value: true, name: 'Code'},
        desc: {value: true, name: 'Description'},
        suppCode: {value: true, name: 'suppCode'},
        barcode: {value: true, name: 'barcode'},
        binL: {value: true, name: 'binL'},
        regularSuppCode: {value: true, name: 'regularSuppCode'},
      };

      for (let page = 1; page <= totalPages; page++) {
        const result = await firstValueFrom(
          this.typeSenseService.searchStockItems(
            this.currentSearch ?? '',
            page,
            pageSize,
            store,
            searchableFields,
            this.filters,
            undefined,
            this.selectedAdvancedFilter ?? undefined,
          ),
        );
        const items = mapTypeSenseObjectToGeneric<StockItem>(result);
        for (const item of items) {
          if (this.selectedStockItemsMap.size >= remainingSlots) {
            break;
          }
          this.selectedStockItemsMap.set(item.code, item);
        }
        if (this.selectedStockItemsMap.size >= remainingSlots) {
          break;
        }
      }

      const hitSelectionLimit = this.selectedStockItemsMap.size >= remainingSlots;
      this.restoreSelectedRows();
      this.cdr.detectChanges();
      if (hitSelectionLimit) {
        this.showItemLimitReachedModal();
      }
    } catch (error) {
      console.error('Error selecting all items:', error);
    } finally {
      this.isSelectingAll = false;
      this.cdr.detectChanges();
    }
  }

  onPrintAllVisibleClick(): void {
    if (!this.gridApi) {
      return;
    }
    const itemsToPrint: StockItem[] = [];
    const startIndex = ((this.gridApi.paginationGetCurrentPage() + 1) * this.gridApi.paginationGetPageSize()) - this.gridApi.paginationGetPageSize();
    const stopIndex = ((this.gridApi.paginationGetCurrentPage() + 1) * this.gridApi.paginationGetPageSize());

    for (let i = startIndex; i < stopIndex; i++) {
      if (this.gridApi.getDisplayedRowAtIndex(i)) {
        itemsToPrint.push(this.gridApi.getDisplayedRowAtIndex(i).data as StockItem);
      }
    }
    this.stockItemsSelected.emit(itemsToPrint);
    this.selectedStockItemsMap = new Map();
    this.gridApi.deselectAll();
  }

  getDisplayedCount(): number {
    if (this.gridApi) {
      const startIndex = ((this.gridApi.paginationGetCurrentPage() + 1) * this.gridApi.paginationGetPageSize()) - this.gridApi.paginationGetPageSize();
      const stopIndex = ((this.gridApi.paginationGetCurrentPage() + 1) * this.gridApi.paginationGetPageSize());

      if (this.totalItems > this.gridApi.paginationGetPageSize()) {

        if (stopIndex < this.totalItems) {
          return this.gridApi.paginationGetPageSize();

        }
        if (stopIndex > this.totalItems) {
          return startIndex + (this.totalItems - stopIndex);
        }
      } else return this.totalItems;
    }
    return 0;
  }

  onPrintOtherItemsClick(): void {
    this.printOtherItems.emit();
  }

  onShelfTalkerSettingsClick(event: Record<string, ISelectOption>): void {
    const selectedOptions = Object.entries(event)
      .filter(([key, option]) => option.selected)
      .reduce((acc, [key, option]) => {
        acc[key] = option;
        return acc;
      }, {} as Record<string, ISelectOption>);
    this.shelfTalkerSettings.emit(Object.keys(selectedOptions)[0]);
  }
}
